import type { ArrivalReason } from '@/features/arrival/types';

import { kioskFetch } from './kioskFetch';

/**
 * The arrival-reason selection, posted to the api [KID-5].
 *
 * ⚠️ **These types are hand-written, and the convention (§ 6) says they should be generated** from the
 * api's OpenAPI schema via `npm run api:types`. That script reads `../iss-kiosk-api/openapi.json`, which the
 * backend does not emit yet — the same gap `identityLookup.ts` records. The shapes below mirror
 * `ArrivalReasonRequestDto` and `ArrivalReasonResponseModel` field for field.
 *
 * **Nothing about the candidate's record is sent beyond the id of the record itself.** The screen read
 * nothing to compose itself — that is what *static* means — so there is nothing else it could send. No
 * name, no email, no date of birth, no factor.
 */

export interface ArrivalReasonRequest {
  readonly reason: ArrivalReason;
  /**
   * The Bullhorn candidate id from the diagnosis, so the arrival record says *who* as well as *why*.
   *
   * Optional because the api, not this app, decides what it needs to write a record — and because a
   * diagnosis that carried no id must still be able to record a reason rather than fail.
   */
  readonly candidateId?: number;
}

export type ArrivalReasonOutcome =
  /**
   * The api recorded the selection.
   *
   * ⛔ **It carried a `queued` flag until 2026-09-11, and the endpoint no longer queues anybody**
   * [decision 0206]: the queue entry belongs to the explicit help button on the screen the selection leads
   * to, never to the selection. The api's field is `recorded` for the same reason this one is gone — a
   * permanently-false `queued` is an invitation to wire the retired rule back through it.
   */
  | { readonly kind: 'recorded' }
  /**
   * The api REFUSED the request — a 4xx. **Our contract is wrong, and that is not weather.**
   *
   * **Split out of `unreachable` for the reason `candidate.ts` and `payCard.ts` already split theirs:** the
   * two failures differ in the only way that matters to whoever has to fix them. A dead socket is a fact
   * about the building; a 400 is a fact about this code, and it will not get better by itself.
   *
   * ⚠️ **This is the defect that hid a defect.** `ARRIVAL_REASON` declared `seeMyApplications` and the api's
   * enum did not, so every tap on *See what I applied for* was answered
   * `400 — "reason must be one of the following values: …"`. It read here as `unreachable`, which is
   * indistinguishable from the api being down, so nothing said the contract had drifted and the 400 lived
   * in the browser console until the Operator read it there on 2026-09-11.
   *
   * **The candidate's routing does not change** — `ArrivalReasonScreen` does not await this call at all
   * [BR-K7, decision 0046], and a refused queue write is still a staff problem rather than a dead end in
   * front of somebody standing at a kiosk. What changes is that it is now REPORTED rather than absorbed.
   */
  | { readonly kind: 'rejected'; readonly status: number }
  /**
   * The request never produced an answer — offline, timeout, 5xx, unparseable body.
   *
   * **This is never shown to the candidate.** The arrival ledger is a staff surface: a person who has said
   * why they are here is routed to their destination whether or not it was written, because the
   * alternative is a kiosk that has decided it cannot help someone standing in front of it [BR-K7,
   * decision 0046].
   *
   * A **5xx stays here** deliberately: the api being sick is the same fact to this app as the api being
   * unreachable, and neither is something the kiosk got wrong.
   */
  | { readonly kind: 'unreachable' };

const ARRIVAL_REASON_PATH = '/arrival-reason';

/**
 * Shorter than the lookup's 12s. Nothing waits on this: the candidate has already been routed by the time
 * it resolves, so the only thing a long timeout buys is a socket held open on a device that has moved on.
 */
const REQUEST_TIMEOUT_MS = 8_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

/** Parses what the api returned rather than trusting it (§ 6, "validate at the boundary"). */
function readRecorded(body: unknown): boolean | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const candidate = body as Record<string, unknown>;
  return typeof candidate.recorded === 'boolean' ? candidate.recorded : undefined;
}

/**
 * Raises a refused arrival-reason write where somebody can act on it.
 *
 * ⚠️ **`console.error` because it is the only reporting channel this app has today** — there is no telemetry
 * sink, no error reporter and no `logger` in `src/`, and `eslint.config.mjs` allows exactly `warn` and
 * `error` through `no-console`. It is deliberately the same *shape* the api logs its own queue failure in
 * (`msg` · `alert` · `reason`), so that whatever collects these later — a kiosk device console, a browser
 * agent, an error reporter — reads one format from both halves rather than two.
 *
 * **No candidate id, deliberately**, for the reason the api gives at the same seam: the arrival reason is a
 * closed enumeration and is what makes the alert actionable, while the id identifies a person on a shared
 * device's console.
 */
function reportRefusal(reason: ArrivalReason, status: number): void {
  console.error({
    msg: 'the api REFUSED an arrival-reason write — the kiosk and the api disagree about the contract',
    alert: 'arrival_reason_rejected',
    status,
    reason,
  });
}

/**
 * Records the selection.
 *
 * This function never throws. Every failure is a value, for the same reason `lookupIdentity` gives: the
 * caller's job is to put the candidate on a working screen, and an exception escaping into a render tree
 * does the opposite.
 *
 * Complexity: one request, bounded by `REQUEST_TIMEOUT_MS`.
 */
export async function submitArrivalReason(
  request: ArrivalReasonRequest,
): Promise<ArrivalReasonOutcome> {
  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}${ARRIVAL_REASON_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'unreachable' };
  }

  /*
   * A 4xx is the api saying no to THIS request, and nobody standing at the kiosk can do anything about it —
   * which is precisely why it has to be said out loud somewhere. `!response.ok` used to collapse it into
   * `unreachable`, and that is what kept the `seeMyApplications` 400 invisible for three days.
   */
  if (response.status >= 400 && response.status < 500) {
    reportRefusal(request.reason, response.status);
    return { kind: 'rejected', status: response.status };
  }
  if (!response.ok) return { kind: 'unreachable' };

  const body: unknown = await response.json().catch(() => undefined);
  // A body without the field is an api this app does not recognise, which is the same to the caller as no
  // answer at all. The VALUE is deliberately not branched on: `recorded: false` is a staff problem, and the
  // candidate is routed either way [BR-K7, decision 0046].
  return readRecorded(body) === undefined ? { kind: 'unreachable' } : { kind: 'recorded' };
}
